import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowRight, ArrowLeft, Check, Loader2, PartyPopper, GraduationCap, Building2, Sparkles, Crown, Star, Gem, Utensils, CalendarCheck, AlertCircle } from 'lucide-react';
import { bookEvent, getGoogleAuthUrl, getGoogleAuthStatus, getCalendarAvailability } from '@/lib/api';
import { toast } from 'sonner';
import { format, isSameDay, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';

const eventTypes = [
  { id: 'compleanno', label: 'Compleanno', icon: PartyPopper },
  { id: 'laurea', label: 'Laurea', icon: GraduationCap },
  { id: 'aziendale', label: 'Aziendale', icon: Building2 },
  { id: 'addio-celibato', label: 'Addio Celibato / Nubilato', icon: Sparkles },
  { id: 'altro', label: 'Altro Evento', icon: Star },
];

const timeSlots = [
  { id: 'pomeriggio', label: '15:00 - 20:00', name: 'Pomeriggio' },
  { id: 'sera', label: '20:00 - 02:00', name: 'Sera' },
  { id: 'intera', label: 'Giornata + Sera', name: 'Giornata Intera' },
];

// Real packages from presentation
const standardPackages = [
  { 
    id: 'silver', 
    name: 'Silver', 
    price: 30, 
    icon: Star,
    desc: 'Perfetto per aperitivi e celebrazioni',
    includes: [
      'Buffet gourmet con pinsa farcita',
      'Finger food selezionati',
      'Tramezzini e panini artigianali',
      'Patatine fritte e snack premium',
      '1 bottiglia Prosecco superiore ogni 3 persone'
    ]
  },
  { 
    id: 'gold', 
    name: 'Gold', 
    price: 40, 
    icon: Crown,
    desc: 'Il nostro bestseller con drink card',
    includes: [
      'Tutto il pacchetto Silver',
      '1 plateau pasta gourmet dello chef',
      '2 drink card per cocktail premium'
    ]
  },
  { 
    id: 'platinum', 
    name: 'Platinum', 
    price: 50, 
    icon: Gem,
    desc: 'Open bar illimitato incluso',
    includes: [
      'Tutto il pacchetto Gold',
      '2 plateau pasta gourmet',
      'Open bar alcolici illimitato',
      'Soft drink illimitati'
    ]
  },
  { 
    id: 'fusion', 
    name: 'Fusion', 
    price: 60, 
    icon: Utensils,
    desc: 'Esperienza giapponese premium',
    includes: [
      'Buffet giapponese completo',
      'Open bar premium illimitato'
    ]
  },
];

const villaPackages = [
  { 
    id: 'villa-silver', 
    name: 'Your Villa + Silver', 
    priceFrom: 3000, 
    guests: 50,
    desc: 'Uso esclusivo 20:00-02:00'
  },
  { 
    id: 'villa-gold', 
    name: 'Your Villa + Gold', 
    priceFrom: 4500, 
    guests: 70,
    desc: 'Uso esclusivo 20:00-02:00'
  },
  { 
    id: 'villa-platinum', 
    name: 'Your Villa + Platinum', 
    priceFrom: 6000, 
    guests: 100,
    desc: 'Uso esclusivo 20:00-02:00'
  },
];

const extras = [
  { id: 'dj', label: 'DJ Set Professionale' },
  { id: 'performer', label: 'Performer (mangiafuoco, ballerini, trampolieri)' },
  { id: 'spogliarellista', label: 'Spogliarellista' },
  { id: 'hostess', label: 'Hostess e Staff Animazione' },
  { id: 'foto', label: 'Servizio Fotografico con Album' },
  { id: 'decorazioni', label: 'Decorazioni e Set Fotografici' },
  { id: 'gonfiabili', label: 'Gonfiabili e Giochi Acquatici' },
  { id: 'catering-esterno', label: 'Catering Esterno Coordinato' },
];

const TOTAL_STEPS = 7;

export default function EventiPrivati() {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [bookingId, setBookingId] = useState(null);
  const [packageType, setPackageType] = useState('standard'); // 'standard' or 'villa'
  
  // Google Calendar state
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleEmail, setGoogleEmail] = useState('');
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [availability, setAvailability] = useState({}); // { 'YYYY-MM-DD': 'free'|'partial'|'busy' }
  
  const [formData, setFormData] = useState({
    eventType: '',
    date: null,
    timeSlot: '',
    guests: '',
    package: '',
    extras: [],
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    contactPreference: 'whatsapp',
    notes: '',
  });

  const fetchAvailability = useCallback(async () => {
    setCalendarLoading(true);
    try {
      const data = await getCalendarAvailability();
      const availMap = {};
      data.dates.forEach(d => {
        availMap[d.date] = d.status;
      });
      setAvailability(availMap);
    } catch (error) {
      if (error.response?.status === 401) {
        setGoogleConnected(false);
      }
    } finally {
      setCalendarLoading(false);
    }
  }, []);

  // Check Google auth status on mount and handle callback
  useEffect(() => {
    const checkGoogleAuth = async () => {
      // Check URL for callback params
      const params = new URLSearchParams(window.location.search);
      if (params.get('google_connected') === 'true') {
        const email = params.get('email');
        setGoogleConnected(true);
        setGoogleEmail(email || '');
        toast.success('Google Calendar collegato con successo!');
        // Clean URL
        window.history.replaceState({}, '', window.location.pathname);
        // Fetch availability
        fetchAvailability();
        return;
      }
      
      // Check existing auth status
      try {
        const status = await getGoogleAuthStatus();
        setGoogleConnected(status.connected);
        setGoogleEmail(status.email || '');
        if (status.connected) {
          fetchAvailability();
        }
      } catch {
        // Google auth check non-critical - user can still browse without calendar
        setGoogleConnected(false);
      }
    };
    
    checkGoogleAuth();
  }, [fetchAvailability]);

  const handleConnectGoogle = async () => {
    try {
      const { authorization_url } = await getGoogleAuthUrl();
      window.location.href = authorization_url;
    } catch (error) {
      toast.error('Errore durante la connessione a Google');
    }
  };

  // Get date availability status
  const getDateStatus = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return availability[dateStr] || 'unknown';
  };

  const updateFormData = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const toggleExtra = (extraId) => {
    setFormData(prev => ({
      ...prev,
      extras: prev.extras.includes(extraId)
        ? prev.extras.filter(id => id !== extraId)
        : [...prev.extras, extraId]
    }));
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return formData.eventType !== '';
      case 2: return formData.date !== null && formData.timeSlot !== '';
      case 3: return formData.guests !== '' && parseInt(formData.guests) >= 30;
      case 4: return formData.package !== '';
      case 5: return true;
      case 6: return formData.contactName && formData.contactEmail && formData.contactPhone;
      case 7: return true;
      default: return false;
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const response = await bookEvent({
        event_type: formData.eventType,
        date: format(formData.date, 'yyyy-MM-dd'),
        time_slot: formData.timeSlot,
        guests: parseInt(formData.guests),
        package: formData.package,
        extras: formData.extras,
        contact_name: formData.contactName,
        contact_email: formData.contactEmail,
        contact_phone: formData.contactPhone,
        contact_preference: formData.contactPreference,
        notes: formData.notes,
      });
      setBookingId(response.booking_id);
      setSubmitted(true);
      toast.success('Richiesta inviata con successo!');
    } catch (error) {
      toast.error('Errore durante l\'invio. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  const selectedPackageDetails = packageType === 'standard' 
    ? standardPackages.find(p => p.id === formData.package)
    : villaPackages.find(p => p.id === formData.package);

  if (submitted) {
    return (
      <div className="bg-ivory pt-32 min-h-screen" data-testid="booking-success">
        <div className="max-w-xl mx-auto px-6 text-center py-24">
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", duration: 0.8 }}
            className="w-20 h-20 bg-champagne rounded-full flex items-center justify-center mx-auto mb-8"
          >
            <Check className="text-white" size={40} />
          </motion.div>
          <motion.h1 
            className="font-heading text-4xl text-night mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            Richiesta Inviata!
          </motion.h1>
          <motion.p 
            className="font-body text-stone-600 mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            Grazie per aver scelto La Villa. Ti contatteremo entro 24 ore per confermare i dettagli del tuo evento.
          </motion.p>
          {bookingId && (
            <motion.p 
              className="font-body text-sm text-stone-500 mb-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              Codice prenotazione: <strong className="text-night">{bookingId}</strong>
            </motion.p>
          )}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <Button
              asChild
              className="bg-night text-ivory hover:bg-stone-800 rounded-full px-8 py-6 text-sm uppercase tracking-widest"
            >
              <a href="/">Torna alla Home</a>
            </Button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-ivory pt-32 min-h-screen" data-testid="eventi-privati-page">
      {/* Header */}
      <section className="pb-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-3xl mx-auto text-center">
          <motion.p
            className="font-body text-xs uppercase tracking-[0.3em] text-champagne mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            Richiedi Preventivo
          </motion.p>
          <motion.h1
            className="font-heading text-4xl md:text-5xl text-night tracking-tight mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="page-title"
          >
            Eventi Privati
          </motion.h1>
          <motion.p
            className="font-body text-stone-500"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            Crea il tuo evento perfetto a La Villa
          </motion.p>
        </div>
      </section>

      {/* Progress Bar */}
      <section className="pb-12 px-6">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <motion.div
                key={`step-${i}`}
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-body transition-all ${
                  i + 1 < currentStep
                    ? 'bg-champagne text-white'
                    : i + 1 === currentStep
                    ? 'bg-night text-ivory'
                    : 'bg-stone-200 text-stone-500'
                }`}
                initial={{ scale: 0.8 }}
                animate={{ scale: i + 1 === currentStep ? 1.1 : 1 }}
                transition={{ type: "spring" }}
              >
                {i + 1 < currentStep ? <Check size={16} /> : i + 1}
              </motion.div>
            ))}
          </div>
          <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-champagne to-gold"
              initial={{ width: 0 }}
              animate={{ width: `${((currentStep - 1) / (TOTAL_STEPS - 1)) * 100}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>
      </section>

      {/* Wizard Content */}
      <section className="pb-24 px-6">
        <div className="max-w-4xl mx-auto">
          <AnimatePresence mode="wait">
            <motion.div 
              key={currentStep}
              className="bg-white p-8 md:p-12 shadow-soft min-h-[450px]"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.3 }}
            >
              {/* Step 1: Event Type */}
              {currentStep === 1 && (
                <div data-testid="step-1">
                  <h2 className="font-heading text-2xl text-night mb-2">Tipo di Evento</h2>
                  <p className="font-body text-stone-500 mb-8">Che tipo di evento stai organizzando?</p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {eventTypes.map((type, i) => (
                      <motion.button
                        key={type.id}
                        onClick={() => updateFormData('eventType', type.id)}
                        className={`p-6 border text-left transition-all ${
                          formData.eventType === type.id
                            ? 'border-champagne bg-champagne/5'
                            : 'border-stone-200 hover:border-stone-300'
                        }`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.05 }}
                        whileHover={{ y: -2 }}
                        data-testid={`event-type-${type.id}`}
                      >
                        <type.icon
                          className={formData.eventType === type.id ? 'text-champagne mb-3' : 'text-stone-400 mb-3'}
                          size={28}
                          strokeWidth={1.5}
                        />
                        <p className="font-heading text-base md:text-lg text-night leading-tight text-center">{type.label}</p>
                      </motion.button>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 2: Date & Time */}
              {currentStep === 2 && (
                <div data-testid="step-2">
                  <h2 className="font-heading text-2xl text-night mb-2">Data e Orario</h2>
                  <p className="font-body text-stone-500 mb-6">Quando vorresti organizzare l'evento?</p>
                  
                  {/* Google Calendar Connection Banner */}
                  {!googleConnected && (
                    <div 
                      className="bg-gradient-to-r from-champagne/10 to-gold/10 border border-champagne/30 p-4 mb-6 flex items-center justify-between"
                      data-testid="google-connect-banner"
                    >
                      <div className="flex items-center gap-3">
                        <CalendarCheck className="text-champagne" size={24} />
                        <div>
                          <p className="font-body text-sm text-night font-semibold">Collega Google Calendar</p>
                          <p className="font-body text-xs text-stone-500">Visualizza automaticamente le date disponibili</p>
                        </div>
                      </div>
                      <Button
                        onClick={handleConnectGoogle}
                        variant="outline"
                        size="sm"
                        className="border-champagne text-champagne hover:bg-champagne hover:text-white"
                        data-testid="connect-google-btn"
                      >
                        Connetti
                      </Button>
                    </div>
                  )}
                  
                  {googleConnected && (
                    <motion.div 
                      className="bg-green-50 border border-green-200 p-3 mb-6 flex items-center gap-3"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <Check className="text-green-600" size={18} />
                      <p className="font-body text-sm text-green-800">
                        Calendario collegato
                      </p>
                    </motion.div>
                  )}
                  
                  {/* Availability Legend */}
                  {googleConnected && (
                    <div className="flex flex-wrap gap-4 mb-6 p-3 bg-stone-50 border border-stone-200">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-green-500"></div>
                        <span className="font-body text-xs text-stone-600">Disponibile</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-orange-500"></div>
                        <span className="font-body text-xs text-stone-600">Parzialmente disponibile</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded-full bg-red-500"></div>
                        <span className="font-body text-xs text-stone-600">Occupato</span>
                      </div>
                    </div>
                  )}
                  
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <p className="font-body text-sm text-stone-600 mb-3">Seleziona Data</p>
                      <div className="calendar-premium relative">
                        {calendarLoading && (
                          <div className="absolute inset-0 bg-white/80 z-10 flex items-center justify-center">
                            <Loader2 className="animate-spin text-champagne" size={24} />
                          </div>
                        )}
                        <Calendar
                          mode="single"
                          selected={formData.date}
                          onSelect={(date) => {
                            if (date) {
                              const status = getDateStatus(date);
                              if (status === 'busy' && googleConnected) {
                                toast.error('Questa data è già prenotata');
                                return;
                              }
                              updateFormData('date', date);
                            }
                          }}
                          disabled={(date) => date < new Date()}
                          className="rounded-md border border-stone-200"
                          locale={it}
                          modifiers={{
                            free: (date) => googleConnected && getDateStatus(date) === 'free',
                            partial: (date) => googleConnected && getDateStatus(date) === 'partial',
                            busy: (date) => googleConnected && getDateStatus(date) === 'busy',
                          }}
                          modifiersClassNames={{
                            free: 'bg-green-100 text-green-800 hover:bg-green-200 font-semibold',
                            partial: 'bg-orange-100 text-orange-800 hover:bg-orange-200 font-semibold',
                            busy: 'bg-red-100 text-red-800 hover:bg-red-200 cursor-not-allowed opacity-60',
                          }}
                        />
                      </div>
                      {formData.date && (
                        <motion.div 
                          className="mt-3"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                        >
                          <p className="font-body text-sm text-champagne">
                            Data selezionata: {format(formData.date, 'dd MMMM yyyy', { locale: it })}
                          </p>
                          {googleConnected && getDateStatus(formData.date) === 'partial' && (
                            <p className="font-body text-xs text-orange-600 mt-1 flex items-center gap-1">
                              <AlertCircle size={12} />
                              Questa data ha alcuni eventi, verifica disponibilità
                            </p>
                          )}
                        </motion.div>
                      )}
                    </div>
                    
                    <div>
                      <p className="font-body text-sm text-stone-600 mb-3">Fascia Oraria</p>
                      <div className="space-y-3">
                        {timeSlots.map((slot, i) => (
                          <motion.button
                            key={slot.id}
                            type="button"
                            onClick={() => updateFormData('timeSlot', slot.id)}
                            className={`w-full flex items-center space-x-3 p-4 border text-left transition-all ${
                              formData.timeSlot === slot.id
                                ? 'border-champagne bg-champagne/10'
                                : 'border-stone-200 hover:border-stone-300'
                            }`}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.1 }}
                            whileHover={{ x: 5 }}
                            data-testid={`time-slot-${slot.id}`}
                          >
                            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                              formData.timeSlot === slot.id ? 'border-champagne' : 'border-stone-300'
                            }`}>
                              {formData.timeSlot === slot.id && (
                                <motion.div 
                                  className="w-2 h-2 rounded-full bg-champagne"
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                />
                              )}
                            </div>
                            <div className="flex-1">
                              <p className="font-body font-semibold text-night">{slot.name}</p>
                              <p className="font-body text-xs text-stone-500">{slot.label}</p>
                            </div>
                          </motion.button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Guests */}
              {currentStep === 3 && (
                <div data-testid="step-3">
                  <h2 className="font-heading text-2xl text-night mb-2">Numero Ospiti</h2>
                  <p className="font-body text-stone-500 mb-8">Quante persone parteciperanno? (minimo 30)</p>
                  
                  <div className="max-w-xs mx-auto">
                    <Input
                      type="number"
                      min="30"
                      max="2000"
                      placeholder="Es. 50"
                      value={formData.guests}
                      onChange={(e) => updateFormData('guests', e.target.value)}
                      className="text-center text-3xl py-8 font-heading"
                      data-testid="guests-input"
                    />
                    <p className="font-body text-xs text-stone-400 mt-2 text-center">
                      Minimo 30 persone per tutti i pacchetti
                    </p>
                    {formData.guests && parseInt(formData.guests) < 30 && (
                      <motion.p 
                        className="font-body text-xs text-red-500 mt-2 text-center"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                      >
                        Il numero minimo di ospiti è 30
                      </motion.p>
                    )}
                  </div>
                </div>
              )}

              {/* Step 4: Package */}
              {currentStep === 4 && (
                <div data-testid="step-4">
                  <h2 className="font-heading text-2xl text-night mb-2">Pacchetto</h2>
                  <p className="font-body text-stone-500 mb-6">Scegli il pacchetto più adatto</p>
                  
                  {/* Package Type Toggle */}
                  <div className="flex gap-2 mb-8">
                    <button
                      onClick={() => { setPackageType('standard'); updateFormData('package', ''); }}
                      className={`flex-1 py-3 px-4 text-sm font-body uppercase tracking-wider transition-all ${
                        packageType === 'standard'
                          ? 'bg-night text-ivory'
                          : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                      }`}
                    >
                      Pacchetti Standard
                    </button>
                    <button
                      onClick={() => { setPackageType('villa'); updateFormData('package', ''); }}
                      className={`flex-1 py-3 px-4 text-sm font-body uppercase tracking-wider transition-all ${
                        packageType === 'villa'
                          ? 'bg-night text-ivory'
                          : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                      }`}
                    >
                      Your Villa (Esclusiva)
                    </button>
                  </div>
                  
                  <AnimatePresence mode="wait">
                    {packageType === 'standard' ? (
                      <motion.div 
                        key="standard"
                        className="grid sm:grid-cols-2 gap-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        {standardPackages.map((pkg, i) => (
                          <motion.button
                            key={pkg.id}
                            onClick={() => updateFormData('package', pkg.id)}
                            className={`p-6 border text-left transition-all ${
                              formData.package === pkg.id
                                ? 'border-champagne bg-champagne/5'
                                : 'border-stone-200 hover:border-stone-300'
                            }`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.05 }}
                            whileHover={{ y: -2 }}
                            data-testid={`package-${pkg.id}`}
                          >
                            <pkg.icon className={`mb-3 ${formData.package === pkg.id ? 'text-champagne' : 'text-stone-400'}`} size={24} />
                            <p className="font-heading text-xl text-night mb-1">{pkg.name}</p>
                            <p className="font-body text-champagne font-semibold mb-2">€{pkg.price} a persona</p>
                            <p className="font-body text-xs text-stone-500">{pkg.desc}</p>
                          </motion.button>
                        ))}
                      </motion.div>
                    ) : (
                      <motion.div 
                        key="villa"
                        className="space-y-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        <div className="bg-champagne/10 p-4 mb-4">
                          <p className="font-body text-sm text-night">
                            <strong>Your Villa</strong> include: uso esclusivo della location (20:00-02:00), piscina con illuminazione notturna, area bar attrezzata, arredi, consolle DJ e impianto audio, staff di sicurezza/accoglienza/pulizie, parcheggio riservato.
                          </p>
                        </div>
                        {villaPackages.map((pkg, i) => (
                          <motion.button
                            key={pkg.id}
                            onClick={() => updateFormData('package', pkg.id)}
                            className={`w-full p-6 border text-left transition-all ${
                              formData.package === pkg.id
                                ? 'border-champagne bg-champagne/5'
                                : 'border-stone-200 hover:border-stone-300'
                            }`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.05 }}
                            whileHover={{ x: 5 }}
                            data-testid={`package-${pkg.id}`}
                          >
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-heading text-xl text-night mb-1">{pkg.name}</p>
                                <p className="font-body text-xs text-stone-500">{pkg.desc} • fino a {pkg.guests} ospiti</p>
                              </div>
                              <p className="font-body text-champagne font-semibold">da €{pkg.priceFrom.toLocaleString()}</p>
                            </div>
                          </motion.button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                  
                  <p className="font-body text-xs text-stone-400 mt-6 text-center">
                    Contributo fisso pulizie e utenze: €250 (per tutti i pacchetti)
                  </p>
                </div>
              )}

              {/* Step 5: Extras */}
              {currentStep === 5 && (
                <div data-testid="step-5">
                  <h2 className="font-heading text-2xl text-night mb-2">Servizi Extra</h2>
                  <p className="font-body text-stone-500 mb-8">Aggiungi servizi per rendere il tuo evento ancora più speciale</p>
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    {extras.map((extra, i) => (
                      <motion.div
                        key={extra.id}
                        onClick={() => toggleExtra(extra.id)}
                        className={`flex items-center gap-4 p-4 border cursor-pointer transition-all ${
                          formData.extras.includes(extra.id)
                            ? 'border-champagne bg-champagne/5'
                            : 'border-stone-200 hover:border-stone-300'
                        }`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.03 }}
                        whileHover={{ x: 3 }}
                        data-testid={`extra-${extra.id}`}
                      >
                        <Checkbox
                          checked={formData.extras.includes(extra.id)}
                          className="pointer-events-none"
                        />
                        <span className="font-body text-sm text-night">{extra.label}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 6: Contact */}
              {currentStep === 6 && (
                <div data-testid="step-6">
                  <h2 className="font-heading text-2xl text-night mb-2">Contatti</h2>
                  <p className="font-body text-stone-500 mb-8">Come possiamo contattarti?</p>
                  
                  <div className="space-y-6 max-w-md mx-auto">
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                    >
                      <Label className="font-body text-sm text-stone-600">Nome e Cognome</Label>
                      <Input
                        value={formData.contactName}
                        onChange={(e) => updateFormData('contactName', e.target.value)}
                        placeholder="Mario Rossi"
                        className="mt-2"
                        data-testid="contact-name-input"
                      />
                    </motion.div>
                    
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                    >
                      <Label className="font-body text-sm text-stone-600">Email</Label>
                      <Input
                        type="email"
                        value={formData.contactEmail}
                        onChange={(e) => updateFormData('contactEmail', e.target.value)}
                        placeholder="mario@esempio.com"
                        className="mt-2"
                        data-testid="contact-email-input"
                      />
                    </motion.div>
                    
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                    >
                      <Label className="font-body text-sm text-stone-600">Telefono</Label>
                      <Input
                        type="tel"
                        value={formData.contactPhone}
                        onChange={(e) => updateFormData('contactPhone', e.target.value)}
                        placeholder="+39 333 123 4567"
                        className="mt-2"
                        data-testid="contact-phone-input"
                      />
                    </motion.div>
                    
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                    >
                      <Label className="font-body text-sm text-stone-600 mb-3 block">
                        Preferenza di Contatto
                      </Label>
                      <Select
                        value={formData.contactPreference}
                        onValueChange={(value) => updateFormData('contactPreference', value)}
                      >
                        <SelectTrigger data-testid="contact-preference-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="whatsapp">WhatsApp</SelectItem>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="telefono">Telefonata</SelectItem>
                        </SelectContent>
                      </Select>
                    </motion.div>
                  </div>
                </div>
              )}

              {/* Step 7: Summary */}
              {currentStep === 7 && (
                <div data-testid="step-7">
                  <h2 className="font-heading text-2xl text-night mb-2">Riepilogo</h2>
                  <p className="font-body text-stone-500 mb-8">Verifica i dettagli della tua richiesta</p>
                  
                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="space-y-4">
                      {[
                        { label: 'Tipo Evento', value: eventTypes.find(e => e.id === formData.eventType)?.label },
                        { label: 'Data', value: formData.date && format(formData.date, 'dd MMMM yyyy', { locale: it }) },
                        { label: 'Orario', value: timeSlots.find(t => t.id === formData.timeSlot)?.name },
                        { label: 'Ospiti', value: `${formData.guests} persone` },
                        { label: 'Pacchetto', value: selectedPackageDetails?.name },
                        { label: 'Contatto', value: formData.contactName },
                      ].map((item, i) => (
                        <motion.div 
                          key={item.label}
                          className="flex justify-between py-3 border-b border-stone-100"
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: i * 0.05 }}
                        >
                          <span className="font-body text-stone-500">{item.label}</span>
                          <span className="font-body text-night text-right">{item.value}</span>
                        </motion.div>
                      ))}
                      {formData.extras.length > 0 && (
                        <motion.div 
                          className="flex justify-between py-3 border-b border-stone-100"
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.3 }}
                        >
                          <span className="font-body text-stone-500">Extra</span>
                          <span className="font-body text-night text-right text-sm">
                            {formData.extras.map(e => extras.find(x => x.id === e)?.label).join(', ')}
                          </span>
                        </motion.div>
                      )}
                    </div>
                    
                    <div>
                      <Label className="font-body text-sm text-stone-600">Note Aggiuntive</Label>
                      <Textarea
                        value={formData.notes}
                        onChange={(e) => updateFormData('notes', e.target.value)}
                        placeholder="Richieste particolari, dettagli aggiuntivi..."
                        className="mt-2"
                        rows={6}
                        data-testid="notes-textarea"
                      />
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex justify-between mt-8">
            <Button
              variant="outline"
              onClick={() => setCurrentStep(prev => prev - 1)}
              disabled={currentStep === 1}
              className="rounded-full px-6"
              data-testid="prev-button"
            >
              <ArrowLeft className="mr-2" size={16} />
              Indietro
            </Button>
            
            {currentStep < TOTAL_STEPS ? (
              <Button
                onClick={() => setCurrentStep(prev => prev + 1)}
                disabled={!canProceed()}
                className="bg-night text-ivory hover:bg-stone-800 rounded-full px-6"
                data-testid="next-button"
              >
                Avanti
                <ArrowRight className="ml-2" size={16} />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={loading}
                className="bg-champagne text-white hover:bg-gold rounded-full px-8"
                data-testid="submit-button"
              >
                {loading ? (
                  <Loader2 className="animate-spin" size={20} />
                ) : (
                  <>
                    Invia Richiesta
                    <ArrowRight className="ml-2" size={16} />
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Bottom padding for mobile CTA */}
      <div className="h-20 lg:hidden" />
    </div>
  );
}
